import * as React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  CircularProgress,
  Skeleton,
  Alert,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  RemoveCircleOutline as RemoveIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
  createUploadSession,
  extractMetadata,
  checkUploadDuplicates,
} from "../services/api";
import { useAllowedCategories, useCategories } from "../hooks/useCategories";
import { CategorySelectDialog } from "../components/CategorySelectDialog";
import { ConfirmDialog } from "../components/common/ConfirmDialog";
import { DuplicateMatchesList } from "../components/common/DuplicateMatchesList";
import toast from "react-hot-toast";
import type { DuplicateMatch } from "../types";
import {
  isModelUnavailableError,
  MODEL_UNAVAILABLE_MESSAGE,
} from "../utils/llmAvailability";

dayjs.extend(customParseFormat);

export interface FileWithMeta {
  id: string;
  file: File;
  title: string;
  publicationDate: string;
  categoryName: string;
  isMetadataLoading: boolean;
  metadataError: string | null;
  metadataModelUnavailable?: boolean;
  /** Etichetta della categoria rilevata dalla classificazione automatica ma non
   * autorizzata per l'utente corrente: se presente, va mostrato un avviso e la
   * categoria deve essere selezionata manualmente tra quelle consentite. */
  categoryUnauthorizedLabel?: string | null;
}

function normalizeExtractedDate(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const value = raw.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  if (/^\d{4}-\d{2}$/.test(value)) return `${value}-01`;
  if (/^\d{4}$/.test(value)) return `${value}-01-01`;
  return null;
}

function isValidPublicationDate(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) && dayjs(value, "YYYY-MM-DD", true).isValid();
}

function getRowValidationError(
  row: FileWithMeta,
  categories: { value: string }[]
): string | null {
  if (row.isMetadataLoading) return null;
  if (!row.title.trim()) return "Titolo obbligatorio";
  if (!row.publicationDate || !isValidPublicationDate(row.publicationDate)) {
    return "Data di pubblicazione obbligatoria (formato YYYY-MM-DD)";
  }
  if (!row.categoryName.trim()) return "Categoria obbligatoria";
  if (
    categories.length > 0 &&
    !categories.some((c) => c.value === row.categoryName)
  ) {
    return "Categoria non valida";
  }
  return null;
}

export const UploadKnowledgePage: React.FC = () => {
  const navigate = useNavigate();
  const [filesWithMeta, setFilesWithMeta] = React.useState<FileWithMeta[]>([]);
  const {
    data: categories = [],
    isLoading: categoriesLoading,
    isError: categoriesError,
  } = useAllowedCategories();
  // Elenco completo (non filtrato per permessi), usato solo per capire se una
  // categoria rilevata dalla classificazione automatica è reale ma non consentita
  // per l'utente corrente (per mostrare l'avviso di autorizzazione appropriato).
  const { data: allCategories = [] } = useCategories();
  const [uploading, setUploading] = React.useState(false);
  const [checkingDuplicates, setCheckingDuplicates] = React.useState(false);
  const [showValidationErrors, setShowValidationErrors] = React.useState(false);
  const [categoryDialogRowIndex, setCategoryDialogRowIndex] = React.useState<
    number | null
  >(null);
  const [duplicateWarning, setDuplicateWarning] = React.useState<{
    filename: string;
    matches: DuplicateMatch[];
  }[] | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const buildRowId = () =>
    `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

  const defaultCategory = categories[0]?.value ?? "";

  React.useEffect(() => {
    if (!categories.length) return;
    setFilesWithMeta((prev) =>
      prev.map((row) => ({
        ...row,
        categoryName: row.categoryName || categories[0].value,
      }))
    );
  }, [categories]);

  const mapExtractedCategoryToValue = React.useCallback(
    (
      category: string,
      fallback: string
    ): { value: string; unauthorizedLabel: string | null } => {
      const normalized = category.trim().toLowerCase();
      if (!normalized) return { value: fallback, unauthorizedLabel: null };
      const matchedAllowed = categories.find(
        (c) => c.value.trim().toLowerCase() === normalized
      );
      if (matchedAllowed) {
        return { value: matchedAllowed.value, unauthorizedLabel: null };
      }
      // La categoria rilevata esiste ma non è tra quelle consentite per l'utente:
      // non precompilare, segnala esplicitamente e richiedi selezione manuale.
      const matchedAny = allCategories.find(
        (c) => c.value.trim().toLowerCase() === normalized
      );
      if (matchedAny) {
        return { value: fallback, unauthorizedLabel: matchedAny.label || matchedAny.value };
      }
      return { value: fallback, unauthorizedLabel: null };
    },
    [categories, allCategories]
  );

  const prefillRowMetadata = React.useCallback(
    async (rowId: string, file: File) => {
      try {
        const extracted = await extractMetadata(file);
        const normalizedDate = normalizeExtractedDate(extracted.date);
        setFilesWithMeta((prev) =>
          prev.map((row) => {
            if (row.id !== rowId) return row;
            const { value: categoryValue, unauthorizedLabel } =
              mapExtractedCategoryToValue(
                extracted.category || "",
                row.categoryName || defaultCategory
              );
            return {
              ...row,
              title: extracted.title?.trim() || row.title,
              publicationDate: normalizedDate || row.publicationDate,
              categoryName: categoryValue,
              categoryUnauthorizedLabel: unauthorizedLabel,
              isMetadataLoading: false,
              metadataError: null,
              metadataModelUnavailable: false,
            };
          })
        );
      } catch (err: unknown) {
        const modelUnavailable = isModelUnavailableError(err);
        const message = modelUnavailable
          ? MODEL_UNAVAILABLE_MESSAGE
          : err instanceof Error
            ? err.message
            : "Impossibile estrarre i metadati per questo documento.";
        setFilesWithMeta((prev) =>
          prev.map((row) =>
            row.id === rowId
              ? {
                  ...row,
                  isMetadataLoading: false,
                  metadataError: message,
                  metadataModelUnavailable: modelUnavailable,
                  categoryName: row.categoryName || defaultCategory,
                }
              : row
          )
        );
      }
    },
    [mapExtractedCategoryToValue, defaultCategory]
  );

  const addFiles = (list: FileList) => {
    if (!list.length) return;
    if (categoriesLoading) {
      toast.error("Attendi il caricamento delle categorie");
      return;
    }
    if (categoriesError || categories.length === 0) {
      toast.error(
        "Impossibile caricare le categorie. Ricarica la pagina e riprova."
      );
      return;
    }
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    const next: FileWithMeta[] = [];
    for (let i = 0; i < list.length; i++) {
      const file = list[i];
      if (file.type !== "application/pdf") {
        toast.error(`"${file.name}" non è un PDF valido`);
        continue;
      }
      const name = file.name.replace(/\.[^/.]+$/, "") || file.name;
      next.push({
        id: buildRowId(),
        file,
        title: name,
        publicationDate: `${yyyy}-${mm}-${dd}`,
        categoryName: defaultCategory,
        isMetadataLoading: true,
        metadataError: null,
        metadataModelUnavailable: false,
      });
    }
    if (!next.length) return;
    setFilesWithMeta((prev) => [...prev, ...next]);
    void (async () => {
      for (const row of next) {
        await prefillRowMetadata(row.id, row.file);
      }
    })();
  };

  const handleSelectFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) addFiles(e.target.files);
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files) addFiles(e.dataTransfer.files);
  };

  const removeRow = (index: number) => {
    setFilesWithMeta((prev) => prev.filter((_, i) => i !== index));
  };

  const updateRow = (
    index: number,
    field: keyof FileWithMeta,
    value: string | File
  ) => {
    setFilesWithMeta((prev) =>
      prev.map((row, i) =>
        i === index
          ? {
              ...row,
              [field]: value,
              ...(field !== "metadataError" && field !== "metadataModelUnavailable"
                ? { metadataError: null, metadataModelUnavailable: false }
                : {}),
              ...(field === "categoryName" ? { categoryUnauthorizedLabel: null } : {}),
            }
          : row
      )
    );
  };

  const allRowsValid =
    filesWithMeta.length > 0 &&
    !filesWithMeta.some((row) => row.isMetadataLoading) &&
    filesWithMeta.every((row) => !getRowValidationError(row, categories));

  const doUpload = async () => {
    setUploading(true);
    try {
      const files = filesWithMeta.map((r) => r.file);
      const metadata = filesWithMeta.map((r) => ({
        title: r.title.trim(),
        publication_date: r.publicationDate,
        category_id: r.categoryName,
      }));
      await createUploadSession(files, metadata);
      toast.success("Caricamento avviato");
      setFilesWithMeta([]);
      setShowValidationErrors(false);
      navigate("/knowledge-base");
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      const message =
        (typeof detail === "string" && detail) ||
        err?.message ||
        "Errore durante il caricamento";
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  const handleStartUpload = async () => {
    if (filesWithMeta.length === 0) {
      toast.error("Seleziona almeno un file");
      return;
    }
    setShowValidationErrors(true);
    if (!allRowsValid) {
      toast.error(
        "Compila titolo, data (YYYY-MM-DD) e categoria per ogni documento prima di avviare il caricamento"
      );
      return;
    }
    setCheckingDuplicates(true);
    try {
      const files = filesWithMeta.map((r) => r.file);
      const metadata = filesWithMeta.map((r) => ({
        title: r.title.trim(),
        publication_date: r.publicationDate,
        category_id: r.categoryName,
      }));
      const { results } = await checkUploadDuplicates(files, metadata);
      const withMatches = results.filter((r) => r.matches.length > 0);
      if (withMatches.length > 0) {
        setDuplicateWarning(withMatches);
        return;
      }
    } catch (err) {
      // Il controllo duplicati non deve bloccare il caricamento se fallisce (es. servizio non disponibile)
      console.error("[UploadKnowledge] check-duplicates failed:", err);
    } finally {
      setCheckingDuplicates(false);
    }
    await doUpload();
  };

  return (
    <Box
      sx={{
        p: 3,
        bgcolor: "#F5F5F5",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      <Typography variant="h5" fontWeight={600}>
        Nuova sessione di caricamento documenti
      </Typography>

      {categoriesError && (
        <Alert severity="error">
          Impossibile caricare le categorie. Ricarica la pagina per poter
          caricare documenti.
        </Alert>
      )}

      {/* Sezione caricamento file */}
      <Paper sx={{ p: 3, borderRadius: 4 }}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Carica un documento
        </Typography>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,application/pdf"
          multiple
          style={{ display: "none" }}
          onChange={handleSelectFiles}
        />
        <Box
          onClick={() => !categoriesLoading && fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          sx={{
            border: "2px dashed",
            borderColor: "divider",
            borderRadius: 2,
            p: 4,
            textAlign: "center",
            cursor: categoriesLoading || categoriesError ? "not-allowed" : "pointer",
            opacity: categoriesLoading || categoriesError ? 0.6 : 1,
            "&:hover": {
              borderColor: categoriesLoading || categoriesError ? "divider" : "primary.main",
              bgcolor: categoriesLoading || categoriesError ? "transparent" : "action.hover",
            },
          }}
        >
          <CloudUploadIcon
            sx={{ fontSize: 56, color: "text.secondary", mb: 1 }}
          />
          <Typography color="text.secondary">
            {categoriesLoading
              ? "Caricamento categorie in corso..."
              : "Clicca o trascina i file qui (PDF)"}
          </Typography>
        </Box>
      </Paper>

      {/* Tabella file selezionati */}
      {filesWithMeta.length > 0 && (
        <Paper sx={{ p: 3, borderRadius: 4 }}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            File selezionati
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Titolo del documento</TableCell>
                <TableCell width={200}>Data Pubblicazione</TableCell>
                <TableCell width={400}>Categoria</TableCell>
                <TableCell width={56} />
              </TableRow>
            </TableHead>
            <TableBody>
              {filesWithMeta.map((row, index) => {
                const validationError = showValidationErrors
                  ? getRowValidationError(row, categories)
                  : null;
                return (
                  <React.Fragment key={row.id}>
                    <TableRow>
                      <TableCell>
                        {row.isMetadataLoading ? (
                          <Skeleton variant="rounded" height={40} />
                        ) : (
                          <TextField
                            size="small"
                            fullWidth
                            value={row.title}
                            onChange={(e) =>
                              updateRow(index, "title", e.target.value)
                            }
                            placeholder="Titolo"
                            error={!!validationError && !row.title.trim()}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {row.isMetadataLoading ? (
                          <Skeleton variant="rounded" height={40} />
                        ) : (
                          <DatePicker
                            value={
                              row.publicationDate
                                ? dayjs(row.publicationDate, "YYYY-MM-DD", true)
                                : null
                            }
                            onChange={(newValue) =>
                              updateRow(
                                index,
                                "publicationDate",
                                newValue && newValue.isValid()
                                  ? newValue.format("YYYY-MM-DD")
                                  : ""
                              )
                            }
                            slotProps={{
                              textField: {
                                size: "small",
                                fullWidth: true,
                                error:
                                  !!validationError &&
                                  !isValidPublicationDate(row.publicationDate),
                              },
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {row.isMetadataLoading ? (
                          <Skeleton variant="rounded" height={40} />
                        ) : (
                          <Button
                            variant="outlined"
                            size="small"
                            color="inherit"
                            fullWidth
                            onClick={() => setCategoryDialogRowIndex(index)}
                            sx={{
                              justifyContent: "space-between",
                              textTransform: "none",
                              height: 40,
                              px: 1.5,
                              py: 0,
                              border: "1px solid",
                              borderColor:
                                validationError && !row.categoryName.trim()
                                  ? "error.main"
                                  : "#cacaca",
                              bgcolor: "background.paper",
                              borderRadius: 1,
                              "&:hover": {
                                borderColor: "black",
                              },
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                textAlign: "left",
                                color: "text.primary",
                              }}
                            >
                              {categories.find((c) => c.value === row.categoryName)
                                ?.label ?? "Seleziona categoria"}
                            </Typography>
                            <ExpandMoreIcon
                              sx={{
                                fontSize: 20,
                                color: "action.active",
                                ml: 0.5,
                                flexShrink: 0,
                              }}
                            />
                          </Button>
                        )}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => removeRow(index)}
                          color="error"
                          disabled={row.isMetadataLoading}
                        >
                          <RemoveIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    {row.metadataError && (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ py: 0, borderBottom: 0 }}>
                          <Alert
                            severity={row.metadataModelUnavailable ? "warning" : "warning"}
                            sx={{ mb: 1 }}
                          >
                            {row.metadataModelUnavailable
                              ? `Servizio AI temporaneamente non disponibile per "${row.file.name}". ${row.metadataError} Compila manualmente titolo, data e categoria, oppure riprova tra qualche minuto.`
                              : `Estrazione automatica fallita per "${row.file.name}": ${row.metadataError}. Compila manualmente titolo, data e categoria.`}
                          </Alert>
                        </TableCell>
                      </TableRow>
                    )}
                    {row.categoryUnauthorizedLabel && !row.metadataError && (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ py: 0, borderBottom: 0 }}>
                          <Alert severity="warning" sx={{ mb: 1 }}>
                            {`Non sei autorizzato a caricare documenti di categoria "${row.categoryUnauthorizedLabel}"; la classificazione automatica ha rilevato questa categoria ma dovrai selezionarne una tra quelle consentite, assumendotene la responsabilità.`}
                          </Alert>
                        </TableCell>
                      </TableRow>
                    )}
                    {validationError && !row.metadataError && (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ py: 0, borderBottom: 0 }}>
                          <Alert severity="error" sx={{ mb: 1 }}>
                            {validationError}
                          </Alert>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Bottone Avvia caricamento */}
      <Paper
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
          borderRadius: 4,
        }}
      >
        <Button
          variant="contained"
          size="large"
          disabled={
            filesWithMeta.length === 0 ||
            uploading ||
            checkingDuplicates ||
            filesWithMeta.some((f) => f.isMetadataLoading) ||
            categoriesLoading ||
            categoriesError
          }
          onClick={handleStartUpload}
          sx={{ width: "100%" }}
          startIcon={
            uploading || checkingDuplicates ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <CloudUploadIcon />
            )
          }
        >
          {uploading
            ? "Caricamento in corso..."
            : checkingDuplicates
              ? "Verifica duplicati in corso..."
              : "Avvia caricamento"}
        </Button>
      </Paper>

      <ConfirmDialog
        open={!!duplicateWarning}
        title="Possibili documenti duplicati"
        content={
          duplicateWarning && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {duplicateWarning.map((r) => (
                <DuplicateMatchesList
                  key={r.filename}
                  fileLabel={r.filename}
                  matches={r.matches}
                />
              ))}
            </Box>
          )
        }
        confirmText="Procedi comunque"
        cancelText="Annulla"
        loading={uploading}
        onCancel={() => setDuplicateWarning(null)}
        onConfirm={async () => {
          setDuplicateWarning(null);
          await doUpload();
        }}
      />

      <CategorySelectDialog
        open={categoryDialogRowIndex !== null}
        onClose={() => setCategoryDialogRowIndex(null)}
        value={
          categoryDialogRowIndex !== null
            ? (filesWithMeta[categoryDialogRowIndex]?.categoryName ?? "")
            : ""
        }
        onConfirm={(value) => {
          if (categoryDialogRowIndex !== null) {
            updateRow(categoryDialogRowIndex, "categoryName", value);
            setCategoryDialogRowIndex(null);
          }
        }}
        title="Seleziona categoria"
        categories={categories}
      />
    </Box>
  );
};
